/* @flow */
import * as React from "react";
import ReactDOM from "react-dom";
import debounce from "lodash/debounce";
import enqueueIn, { cancelEnqueued, type EnqueueRef } from "./utils/enqueueIn";

function distance([x1, y1], [x2, y2]) {
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
}

type PositionAndTarget = {
  clientX: number,
  clientY: number,
  target: EventTarget,
};

type Props = {
  children: ({
    // Whether the button/etc you are rendering should be styled as "active".
    active: boolean,

    // Give the Tappable a ref to the thing you want it to call `onTap`
    // for.
    tapTargetRefHandler: (?HTMLElement) => mixed,
  }) => React.Node,

  // If this is true, then the active state will appear immediately on
  // touchstart/mousedown, instead of after 20ms. If your button/etc is showing
  // up in a scrollable area, then you probably want the 20ms buffer so that
  // stuff doesn't flash active when you scroll around (so leave this false).
  noDelay?: ?boolean,

  // Infrequently, we need to have an event which is "trusted" by the browser,
  // for instance when we open a new app. Don't delay the tap event at all.
  // This doesn't do active styling very well.
  needsTrustedTap?: ?boolean,

  // If you pass this, then it will get called when the target has been tapped,
  // and active will remain true until the Promise you return resolves. This
  // way, you can keep the active state true until the next page loads.
  holdActiveUntil?: ?() => Promise<mixed>,

  // What to do when the element is tapped. We can't use real click events
  // because the timing and details around when and how the browser dispatches
  // click and touch events vary and have some difficult-to-work-around
  // constraints.
  onTap?: ?() => mixed,
};

type State = {
  active: boolean,
};

export default class Tappable extends React.Component<Props, State> {
  state = {
    active: false,
  };

  _isTouchDevice: boolean = "ontouchstart" in window;
  _listeningForUp: boolean = false;
  _isMounted: boolean = false;
  _target: ?HTMLElement = null;
  _startingPositionAndTarget: ?PositionAndTarget = null;
  _handleTapEnqueueRef: ?EnqueueRef;

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
    this._stopListeningForUp();
    cancelEnqueued(this._handleTapEnqueueRef);
  }

  _setStateIfMounted(
    state: $Shape<State> | ((State, Props) => $Shape<State> | void),
    callback: ?() => mixed
  ) {
    if (!this._isMounted) {
      return;
    }
    if (callback == null) {
      this.setState(state);
    } else {
      this.setState(state, callback);
    }
  }

  _targetRefHandler = (el: ?HTMLElement) => {
    const target = this._target;
    if (target != null && el !== target) {
      if (this._isTouchDevice) {
        target.removeEventListener("touchstart", this._handleTouchStart);
      } else {
        target.removeEventListener("mousedown", this._handleMouseDown);
      }
      target.removeEventListener("keyup", this._handleKeyUp);
    }
    if (target !== el && el != null) {
      if (this._isTouchDevice) {
        el.addEventListener("touchstart", this._handleTouchStart);
      } else {
        el.addEventListener("mousedown", this._handleMouseDown);
      }
      el.addEventListener("keyup", this._handleKeyUp);
    }
    this._target = el;
  };

  _listenForUp = () => {
    if (this._listeningForUp) {
      return;
    }
    if (this._isTouchDevice) {
      window.addEventListener("touchend", this._handleTouchEnd);
    } else {
      window.addEventListener("mouseup", this._handleMouseUp);
    }
    window.addEventListener("touchcancel", this._activeOff);
    window.addEventListener("scroll", this._activeOff, true);
    this._listeningForUp = true;
  };

  _stopListeningForUp = () => {
    if (!this._listeningForUp) {
      return;
    }
    if (this._isTouchDevice) {
      window.removeEventListener("touchend", this._handleTouchEnd);
    } else {
      window.removeEventListener("mouseup", this._handleMouseUp);
    }
    window.removeEventListener("touchcancel", this._activeOff);
    window.removeEventListener("scroll", this._activeOff, true);
    this._debouncedSetStateIfMounted.cancel();
    this._listeningForUp = false;
  };

  _handleMouseDown = (event: MouseEvent) => {
    this._startingPositionAndTarget = event;
    this._activeOn();
  };

  _handleTouchStart = (event: TouchEvent) => {
    const touch = event.changedTouches[0];
    this._startingPositionAndTarget = touch;
    this._activeOn();
  };

  _activeOn = () => {
    this._listenForUp();
    if (this.props.noDelay || this.props.needsTrustedTap) {
      this._setStateIfMounted({ active: true });
    } else {
      this._debouncedSetStateIfMounted({ active: true });
    }
  };

  _debouncedSetStateIfMounted = debounce(
    this._setStateIfMounted.bind(this),
    20
  );

  _handleMouseUp = (event: MouseEvent) => {
    this._handleTap(event);
  };

  _handleTouchEnd = (event: TouchEvent) => {
    const touch = event.changedTouches[0];
    const target = document.elementFromPoint(touch.clientX, touch.clientY);
    this._handleTap({
      clientX: touch.clientX,
      clientY: touch.clientY,
      target,
    });
  };

  _handleTap = (end: PositionAndTarget) => {
    if (!this._listeningForUp) {
      // Some event handlers might be fired asynchronously. We don't want to
      // process an event that would cause an up if we're not listening
      // for up anymore.
      return;
    }

    const start = this._startingPositionAndTarget;
    this._startingPositionAndTarget = null;
    const target = this._target;

    if (
      target != null &&
      start != null &&
      start.target instanceof Node &&
      target.contains(start.target) &&
      end.target instanceof Node &&
      target.contains(end.target) &&
      distance([start.clientX, start.clientY], [end.clientX, end.clientY]) < 16
    ) {
      this._executeDelayedTap();
    } else {
      this._activeOff();
    }
  };

  _activeOff = () => {
    if (!this._listeningForUp) {
      // Some event handlers might be fired asynchronously. We don't want to
      // process an event that would cause an up if we're not listening
      // for up anymore.
      return;
    }
    this._stopListeningForUp();
    this._setStateIfMounted({ active: false });
  };

  _executeDelayedTap() {
    this._stopListeningForUp();

    if (this.props.needsTrustedTap) {
      this._afterTapDelay();
      return;
    }

    if (this._handleTapEnqueueRef != null) {
      // A click is already pending for this target.
      return;
    }
    this._handleTapEnqueueRef = enqueueIn(
      () => {
        this._handleTapEnqueueRef = null;
        ReactDOM.unstable_batchedUpdates(() => {
          if (!this._isMounted) {
            return;
          }
          this._afterTapDelay();
        });
      },
      // Our goal here is to try to make sure the change in active gets
      // reflected before the action that occurs when the button is pressed
      // occurs, eg. page transition, menu drawer opening, etc. The change
      // in active will get rendered in between 0 and 20ms, so I'm picking a
      // number bigger than 20 with some wiggle room so that it's not likely
      // that it'll get batched into the same task loop as the task that
      // sets active to true. Also, we don't want this number to be too big
      // or it will feel like the app takes forever to respond to button
      // presses.
      40
    );
  }

  // For screen reader support
  _handleKeyUp = (event: KeyboardEvent) => {
    const enterKeycode = 13;
    const spaceKeycode = 32;
    if (event.keyCode === enterKeycode || event.keyCode === spaceKeycode) {
      this.setState({ active: true });
      this._afterTapDelay();
    }
  };

  _afterTapDelay() {
    const { holdActiveUntil, onTap } = this.props;
    if (onTap != null) {
      onTap();
    }
    if (holdActiveUntil != null) {
      holdActiveUntil().then(() => {
        this._setStateIfMounted({ active: false });
      });
    } else {
      this._setStateIfMounted({ active: false });
    }
  }

  render() {
    const { children } = this.props;
    const { active } = this.state;

    return children({
      active,
      tapTargetRefHandler: this._targetRefHandler,
    });
  }
}

export const ACTIVE_DARKEN_AMOUNT: number = 0.15;

export function simpleActiveBackgroundColor(active: boolean): string {
  if (active) {
    return `rgba(0, 0, 0, ${ACTIVE_DARKEN_AMOUNT})`;
  } else {
    return "transparent";
  }
}



// WEBPACK FOOTER //
// ../nx-components/src/Tappable.jsx